import React from 'react';
import {
  Plus, Pencil, Trash2, ChevronUp, ChevronDown,
  Loader2, FileText, Link as LinkIcon
} from 'lucide-react';
import type { Perfil, Topico } from '../../../types';

interface CatalogTabProps {
  // Video title
  videoTitle: string;
  setVideoTitle: (v: string) => void;
  // Catalog data
  catalog: Perfil[];
  loadingCatalog: boolean;
  // Selection state
  selectedPerfilId: string;
  setSelectedPerfilId: (v: string) => void;
  selectedTopicoId: string;
  setSelectedTopicoId: (v: string) => void;
  selectedSubtopicoId: string;
  setSelectedSubtopicoId: (v: string) => void;
  // Creation inputs
  newPerfilName: string;
  setNewPerfilName: (v: string) => void;
  newTopicoName: string;
  setNewTopicoName: (v: string) => void;
  newSubtopicoName: string;
  setNewSubtopicoName: (v: string) => void;
  // Inline editing
  inlineEditingId: string | null;
  setInlineEditingId: (v: string | null) => void;
  inlineEditingValue: string;
  setInlineEditingValue: (v: string) => void;
  // Handlers
  handleCreatePerfil: (name: string) => void;
  handleRenamePerfil: (id: string, name: string) => void;
  handleDeletePerfil: (id: string) => void;
  handleReorderPerfil: (index: number, direction: 'up' | 'down') => void;
  handleCreateTopico: (name: string) => void;
  handleRenameTopico: (id: string, name: string) => void;
  handleDeleteTopico: (id: string) => void;
  handleReorderTopico: (index: number, direction: 'up' | 'down') => void;
  handleCreateSubtopico: (name: string) => void;
  handleRenameSubtopico: (id: string, name: string) => void;
  handleDeleteSubtopico: (id: string) => void;
  handleReorderSubtopico: (index: number, direction: 'up' | 'down') => void;
}

function CatalogListItem({
  id, label, isSelected, isEditing,
  color, onSelect, onEdit, onDelete, onReorder,
  index, total,
  inlineEditingValue, setInlineEditingValue,
  onConfirmRename, onCancelEdit
}: {
  id: string;
  label: string;
  isSelected: boolean;
  isEditing: boolean;
  color: string;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onReorder: (idx: number, dir: 'up' | 'down') => void;
  index: number;
  total: number;
  inlineEditingValue: string;
  setInlineEditingValue: (v: string) => void;
  onConfirmRename: () => void;
  onCancelEdit: () => void;
}) {
  return (
    <div
      onClick={() => { if (!isEditing) onSelect(); }}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 10px', borderRadius: '6px',
        border: `1px solid ${isSelected ? color : '#1e293b'}`,
        background: isSelected ? `${color}14` : '#0b0f19',
        cursor: 'pointer', transition: 'all 0.2s', gap: '8px'
      }}
    >
      {isEditing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }} onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={inlineEditingValue}
            onChange={(e) => setInlineEditingValue(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onConfirmRename(); }
              else if (e.key === 'Escape') { onCancelEdit(); }
            }}
            style={{ flex: 1, background: '#090d16', border: '1px solid #38bdf8', borderRadius: '4px', padding: '4px 6px', color: '#f8fafc', fontSize: '.72rem' }}
          />
          <button onClick={onConfirmRename} style={{ background: '#22c55e', color: '#0f172a', border: 'none', borderRadius: '4px', padding: '4px 6px', fontSize: '.65rem', fontWeight: 700, cursor: 'pointer' }}>OK</button>
          <button onClick={onCancelEdit} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 6px', fontSize: '.65rem', fontWeight: 700, cursor: 'pointer' }}>X</button>
        </div>
      ) : (
        <>
          <span style={{ fontSize: '.78rem', color: isSelected ? color : '#f8fafc', fontWeight: isSelected ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {label}
          </span>
          <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <button onClick={onEdit} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: '#64748b' }} title="Renomear"><Pencil size={11} /></button>
            {index > 0 && (<button onClick={() => onReorder(index, 'up')} style={{ background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', color: '#64748b' }} title="Subir"><ChevronUp size={12} /></button>)}
            {index < total - 1 && (<button onClick={() => onReorder(index, 'down')} style={{ background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', color: '#64748b' }} title="Descer"><ChevronDown size={12} /></button>)}
            <button onClick={onDelete} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: '#ef4444' }} title="Excluir"><Trash2 size={11} /></button>
          </div>
        </>
      )}
    </div>
  );
}

export function CatalogTab(props: CatalogTabProps) {
  const {
    videoTitle, setVideoTitle,
    catalog, loadingCatalog,
    selectedPerfilId, setSelectedPerfilId,
    selectedTopicoId, setSelectedTopicoId,
    selectedSubtopicoId, setSelectedSubtopicoId,
    newPerfilName, setNewPerfilName,
    newTopicoName, setNewTopicoName,
    newSubtopicoName, setNewSubtopicoName,
    inlineEditingId, setInlineEditingId,
    inlineEditingValue, setInlineEditingValue,
    handleCreatePerfil, handleRenamePerfil, handleDeletePerfil, handleReorderPerfil,
    handleCreateTopico, handleRenameTopico, handleDeleteTopico, handleReorderTopico,
    handleCreateSubtopico, handleRenameSubtopico, handleDeleteSubtopico, handleReorderSubtopico
  } = props;

  const activePerfil = catalog.find(p => p.id === selectedPerfilId);
  const activeTopico = activePerfil?.topicos?.find((t: Topico) => t.id === selectedTopicoId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', maxWidth: '1200px', margin: '0 auto', height: '100%', padding: '10px 4px', overflow: 'hidden' }}>
      <div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.75rem', fontWeight: 600, color: '#94a3b8', marginBottom: 6 }}>
          <FileText size={14} /> Título da Gravação
        </label>
        <input
          type="text"
          required
          value={videoTitle}
          onChange={(e) => setVideoTitle(e.target.value)}
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc', fontSize: '.85rem' }}
        />
      </div>

      <div style={{ background: '#1e293b', padding: 16, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <h4 style={{ fontSize: '.85rem', fontWeight: 800, margin: 0, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 6 }}>
          <LinkIcon size={16} /> Vincular a uma Aula do Catálogo
        </h4>

        {loadingCatalog ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.75rem', color: '#94a3b8', padding: '10px 0' }}>
            <Loader2 size={14} className="animate-spin" /> Carregando catálogo...
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, flex: 1, minHeight: 0 }}>
            {/* Column 1: Perfis */}
            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, height: '100%', minHeight: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '.75rem', fontWeight: 800, color: '#a855f7', textTransform: 'uppercase' }}>Modalidades (Perfis)</span>
                <span style={{ fontSize: '.65rem', color: '#64748b' }}>{catalog.length} itens</span>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="text" placeholder="Nova modalidade..."
                  value={newPerfilName}
                  onChange={(e) => setNewPerfilName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreatePerfil(newPerfilName); } }}
                  style={{ flex: 1, background: '#090d16', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#f8fafc', fontSize: '.75rem' }}
                />
                <button onClick={() => handleCreatePerfil(newPerfilName)} style={{ background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '6px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Plus size={14} /></button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}>
                {([...catalog].sort((a, b) => (a.ordem || 0) - (b.ordem || 0))).map((p, idx, arr) => (
                  <CatalogListItem
                    key={p.id} id={p.id} label={p.nome || p.id}
                    isSelected={selectedPerfilId === p.id}
                    isEditing={inlineEditingId === p.id}
                    color="#a855f7"
                    onSelect={() => { setSelectedPerfilId(p.id); setSelectedTopicoId(''); setSelectedSubtopicoId(''); }}
                    onEdit={() => { setInlineEditingId(p.id); setInlineEditingValue(p.nome || p.id); }}
                    onDelete={() => handleDeletePerfil(p.id)}
                    onReorder={handleReorderPerfil}
                    index={idx} total={arr.length}
                    inlineEditingValue={inlineEditingValue}
                    setInlineEditingValue={setInlineEditingValue}
                    onConfirmRename={() => handleRenamePerfil(p.id, inlineEditingValue)}
                    onCancelEdit={() => setInlineEditingId(null)}
                  />
                ))}
                {catalog.length === 0 && (
                  <div style={{ padding: '20px 10px', textAlign: 'center', color: '#64748b', fontSize: '.72rem', fontStyle: 'italic' }}>
                    Nenhum perfil cadastrado.
                  </div>
                )}
              </div>
            </div>

            {/* Column 2: Tópicos */}
            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, opacity: selectedPerfilId ? 1 : 0.6, height: '100%', minHeight: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '.75rem', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase' }}>Módulos (Tópicos)</span>
                <span style={{ fontSize: '.65rem', color: '#64748b' }}>{activePerfil?.topicos?.length || 0} itens</span>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="text" placeholder="Novo módulo..."
                  disabled={!selectedPerfilId}
                  value={newTopicoName}
                  onChange={(e) => setNewTopicoName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTopico(newTopicoName); } }}
                  style={{ flex: 1, background: selectedPerfilId ? '#090d16' : '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#f8fafc', fontSize: '.75rem', cursor: selectedPerfilId ? 'text' : 'not-allowed' }}
                />
                <button onClick={() => handleCreateTopico(newTopicoName)} disabled={!selectedPerfilId} style={{ background: selectedPerfilId ? '#38bdf8' : '#334155', color: selectedPerfilId ? '#0f172a' : '#64748b', border: 'none', borderRadius: '6px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: selectedPerfilId ? 'pointer' : 'not-allowed' }}><Plus size={14} /></button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}>
                {selectedPerfilId ? (
                  <>
                    {([...(activePerfil?.topicos || [])].sort((a, b) => (a.ordem || 0) - (b.ordem || 0))).map((t, idx, arr) => (
                      <CatalogListItem
                        key={t.id} id={t.id} label={t.titulo}
                        isSelected={selectedTopicoId === t.id}
                        isEditing={inlineEditingId === t.id}
                        color="#38bdf8"
                        onSelect={() => { setSelectedTopicoId(t.id); setSelectedSubtopicoId(''); }}
                        onEdit={() => { setInlineEditingId(t.id); setInlineEditingValue(t.titulo); }}
                        onDelete={() => handleDeleteTopico(t.id)}
                        onReorder={handleReorderTopico}
                        index={idx} total={arr.length}
                        inlineEditingValue={inlineEditingValue}
                        setInlineEditingValue={setInlineEditingValue}
                        onConfirmRename={() => handleRenameTopico(t.id, inlineEditingValue)}
                        onCancelEdit={() => setInlineEditingId(null)}
                      />
                    ))}
                    {(activePerfil?.topicos?.length || 0) === 0 && (
                      <div style={{ padding: '20px 10px', textAlign: 'center', color: '#64748b', fontSize: '.72rem', fontStyle: 'italic' }}>Nenhum módulo cadastrado.</div>
                    )}
                  </>
                ) : (
                  <div style={{ padding: '20px 10px', textAlign: 'center', color: '#64748b', fontSize: '.72rem', fontStyle: 'italic' }}>
                    Selecione uma modalidade ao lado para gerenciar módulos.
                  </div>
                )}
              </div>
            </div>

            {/* Column 3: Subtópicos */}
            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, opacity: selectedTopicoId ? 1 : 0.6, height: '100%', minHeight: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '.75rem', fontWeight: 800, color: '#22c55e', textTransform: 'uppercase' }}>Aulas (Subtópicos)</span>
                <span style={{ fontSize: '.65rem', color: '#64748b' }}>{activeTopico?.subtopicos?.length || 0} itens</span>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="text" placeholder="Nova aula..."
                  disabled={!selectedTopicoId}
                  value={newSubtopicoName}
                  onChange={(e) => setNewSubtopicoName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateSubtopico(newSubtopicoName); } }}
                  style={{ flex: 1, background: selectedTopicoId ? '#090d16' : '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px 10px', color: '#f8fafc', fontSize: '.75rem', cursor: selectedTopicoId ? 'text' : 'not-allowed' }}
                />
                <button onClick={() => handleCreateSubtopico(newSubtopicoName)} disabled={!selectedTopicoId} style={{ background: selectedTopicoId ? '#38bdf8' : '#334155', color: selectedTopicoId ? '#0f172a' : '#64748b', border: 'none', borderRadius: '6px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: selectedTopicoId ? 'pointer' : 'not-allowed' }}><Plus size={14} /></button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}>
                {selectedTopicoId ? (
                  <>
                    {([...(activeTopico?.subtopicos || [])].sort((a, b) => (a.ordem || 0) - (b.ordem || 0))).map((s, idx, arr) => (
                      <CatalogListItem
                        key={s.id} id={s.id} label={s.titulo}
                        isSelected={selectedSubtopicoId === s.id}
                        isEditing={inlineEditingId === s.id}
                        color="#22c55e"
                        onSelect={() => setSelectedSubtopicoId(s.id)}
                        onEdit={() => { setInlineEditingId(s.id); setInlineEditingValue(s.titulo); }}
                        onDelete={() => handleDeleteSubtopico(s.id)}
                        onReorder={handleReorderSubtopico}
                        index={idx} total={arr.length}
                        inlineEditingValue={inlineEditingValue}
                        setInlineEditingValue={setInlineEditingValue}
                        onConfirmRename={() => handleRenameSubtopico(s.id, inlineEditingValue)}
                        onCancelEdit={() => setInlineEditingId(null)}
                      />
                    ))}
                    {(activeTopico?.subtopicos?.length || 0) === 0 && (
                      <div style={{ padding: '20px 10px', textAlign: 'center', color: '#64748b', fontSize: '.72rem', fontStyle: 'italic' }}>Nenhuma aula cadastrada.</div>
                    )}
                  </>
                ) : (
                  <div style={{ padding: '20px 10px', textAlign: 'center', color: '#64748b', fontSize: '.72rem', fontStyle: 'italic' }}>
                    Selecione um módulo ao lado para gerenciar aulas.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
